import { REGEX_VALID_EMAIL } from "utills/constants";
import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import {
  IFipixAccountForm,
  IFipixAccountDetail,
  IOrgEventIdsItem,
} from "interfaces";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Select from "components/select";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";

export interface EditAccountFormProps {
  id: string;
  defaultAccountData: IFipixAccountDetail;
}

const EditFipixAccountSchema = z.object({
  is_active: z.boolean(),
  username: z
    .string()
    .min(1, { message: "This field is required" })
    .refine(
      (value) => REGEX_VALID_EMAIL.test(value ?? ""),
      "Invalid email address"
    ),
  full_name: z
    .string()
    .min(1, "This field is required")
    .max(50, "Text length is too long"),
  events: z.array(z.string()).min(1, "This field is required"),
});

const EVENT_ALL_OPTION = { label: "All", value: "all" };

const EditFipixForm = ({ id, defaultAccountData }: EditAccountFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IFipixAccountForm, HttpError, IFipixAccountForm>({
    defaultValues: {
      events: defaultAccountData.events,
      full_name: defaultAccountData.full_name,
      is_active: defaultAccountData.is_active,
      username: defaultAccountData.username,
    },
    resolver: zodResolver(EditFipixAccountSchema),
  });

  const { mutate, isLoading: isLoading } = useUpdate();
  const navigate = useNavigate();

  const onSubmit = (values: IFipixAccountForm) => {
    const submitValues = { ...values };

    mutate(
      {
        resource: "fipix/accounts",
        id: id,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Edit successfully");
          navigate(`/fipix/accounts/${id}`);
        },
      }
    );
  };

  const {
    data: organizationEventsData,
    isFetching: isFetchingOrganizationEvents,
  } = useOne<IOrgEventIdsItem[], HttpError>({
    resource: "fipix",
    id: `events`,
    meta: {
      resource: "data",
    },
  });

  const organizationEventIds = (
    (organizationEventsData?.data ?? []).length > 0 ? [EVENT_ALL_OPTION] : []
  ).concat(
    (organizationEventsData?.data ?? []).map((event: IOrgEventIdsItem) => ({
      value: event.id,
      label: event.name_en,
    }))
  );

  return (
    <div className="w-full bg-white">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="w-full box-container">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            <div className="flex-1">
              <Label required id="is_active" name="Status" />
              <Controller
                control={control}
                name="is_active"
                defaultValue={true}
                render={({ field: { onChange, value = true, ref } }) => (
                  <SwitchButton checked={value} onChange={onChange} />
                )}
              />
            </div>

            <div className="flex-1">
              <Label required id="username" name="Username (Email)" />
              <Input
                id="username"
                type="text"
                placeholder="Email"
                error={errors["username"]}
                {...register("username")}
              />
              {errors["username"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors?.username.message}
                  </span>
                </p>
              ) : null}
            </div>

            <div className="flex-1">
              <Label required id="full_name" name="Full name" />
              <Input
                id="full_name"
                type="text"
                placeholder="First name"
                maxLength={50}
                error={errors["full_name"]}
                {...register("full_name")}
              />
              <div>
                {errors["full_name"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["full_name"].message}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="w-full mt-[28px]">
            <Label id="events" name="Events" />
            <Controller
              control={control}
              name="events"
              defaultValue={[]}
              render={({ field: { onChange, value = [], ref } }) => (
                <Select
                  isMulti
                  key={`events__${watch("events")}`}
                  inputRef={ref}
                  options={value.includes("all") ? [] : organizationEventIds}
                  id="events"
                  placeholder="Select Event"
                  value={value.map((id: string) => {
                    return organizationEventIds.find(
                      (item) => item.value === id
                    );
                  })}
                  onChange={(selected: { label: string; value: string }[]) =>
                    selected.find(
                      (item) => item.value === EVENT_ALL_OPTION.value
                    )
                      ? onChange([EVENT_ALL_OPTION.value])
                      : onChange(selected.map((item) => `${item.value}`))
                  }
                  error={errors["events"]}
                  isLoading={isFetchingOrganizationEvents}
                />
              )}
            />
            {errors["events"] ? (
              <p className="error-text">
                <span className="font-medium">{errors["events"]?.message}</span>
              </p>
            ) : null}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Link to={"/fipix/accounts"}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                CANCEL
              </Button>
            </Link>
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
        {isLoading ? <FullScreenLoading /> : <></>}
      </form>
    </div>
  );
};

export default EditFipixForm;
